import { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { getConversationName } from '../../utils/chatHelpers';
import CallExtras from './CallExtras';
import MeetingNotesPanel from './MeetingNotesPanel';
import { useCallKeepAlive, IS_TOUCH_DEVICE, LOCK_HINT } from '../../hooks/useCallKeepAlive';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff,
  Maximize2, Minimize2, Monitor, Minus, Phone, UserPlus, X,
  SwitchCamera, Volume2, Bluetooth, PictureInPicture2,
  Wifi, WifiOff, SignalHigh, SignalMedium, SignalLow,
  NotebookPen,
} from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

// Opus DTX: stop sending audio packets during silence (saves ~40% of audio data on a normal conversation).
function withOpusDtx(desc) {
  if (!desc || !desc.sdp) return desc;
  var m = desc.sdp.match(/a=rtpmap:(\d+) opus\/48000/i);
  if (!m) return desc;
  var pt = m[1];
  var re = new RegExp('a=fmtp:' + pt + ' ([^\\r\\n]*)');
  var sdp = re.test(desc.sdp)
    ? desc.sdp.replace(re, function(_, p) { return 'a=fmtp:' + pt + ' ' + (p.indexOf('usedtx') >= 0 ? p : p + ';usedtx=1'); })
    : desc.sdp.replace(new RegExp('(a=rtpmap:' + pt + ' opus/48000[^\\r\\n]*\\r?\\n)', 'i'), '$1a=fmtp:' + pt + ' usedtx=1\r\n');
  return { type: desc.type, sdp: sdp };
}

// Multi-party (mesh) WebRTC call.  Every participant holds one RTCPeerConnection
// per other participant.  Whoever JOINS a call sends the offers to everyone
// already in it; screen-share / track changes renegotiate via
// onnegotiationneeded using the "perfect negotiation" pattern.
export default function VideoCall() {
  var chat = useChat();
  var auth = useAuth();
  var callState = chat.callState;
  var endCall = chat.endCall;
  var sendWebRTCSignal = chat.sendWebRTCSignal;
  var registerHandler = chat.registerHandler;
  var markConnected = chat.markConnected;
  var inviteToCall = chat.inviteToCall;
  var clearPeersToOffer = chat.clearPeersToOffer;

  var myId = (auth && auth.user && auth.user.user_id) || '';
  var callId = callState.callId;
  var callType = callState.callType || 'video';
  var participants = callState.participants || [];
  var _conv = (chat.conversations || []).find(function(c) { return c.conversation_id === callState.conversationId; });
  var recipientName = _conv ? getConversationName(_conv, auth.user && auth.user.user_id) : null;

  var _audio = useState(true); var isAudioEnabled = _audio[0]; var setIsAudioEnabled = _audio[1];
  var _video = useState(callType === 'video'); var isVideoEnabled = _video[0]; var setIsVideoEnabled = _video[1];
  var _screen = useState(false); var isScreenSharing = _screen[0]; var setIsScreenSharing = _screen[1];
  var _full = useState(false); var isFullscreen = _full[0]; var setIsFullscreen = _full[1];
  var _dur = useState(0); var callDuration = _dur[0]; var setCallDuration = _dur[1];
  var _qual = useState({ level: 'checking', latency: 0, packetLoss: 0, jitter: 0, relayed: null });
  var callQuality = _qual[0]; var setCallQuality = _qual[1];
  var _showQ = useState(false); var showQualityDetails = _showQ[0]; var setShowQualityDetails = _showQ[1];
  var _err = useState(null); var mediaError = _err[0]; var setMediaError = _err[1];
  var _min = useState(false); var minimized = _min[0]; var setMinimized = _min[1];
  var _peersUi = useState({}); var peersUi = _peersUi[0]; var setPeersUi = _peersUi[1]; // userId -> {state, stream, name}
  var _addOpen = useState(false); var addOpen = _addOpen[0]; var setAddOpen = _addOpen[1];
  var _notesOpen = useState(false); var notesOpen = _notesOpen[0]; var setNotesOpen = _notesOpen[1];
  var _aiOn = useState(false); var aiOn = _aiOn[0]; var setAiOn = _aiOn[1];
  var _addQ = useState(''); var addQuery = _addQ[0]; var setAddQuery = _addQ[1];
  var _addRes = useState([]); var addResults = _addRes[0]; var setAddResults = _addRes[1];
  var _pillPos = useState(function() {
    try { var raw = localStorage.getItem('bbc:callPillPos'); var p = raw ? JSON.parse(raw) : null; if (p && typeof p.x === 'number' && typeof p.y === 'number') return p; } catch (e) { /* ignore */ }
    return null;
  });
  var pillPos = _pillPos[0]; var setPillPos = _pillPos[1];
  var pillDragRef = useRef({ active: false, moved: false, offX: 0, offY: 0, w: 180, h: 56 });

  var anyConnected = Object.keys(peersUi).some(function(k) { return peersUi[k].state === 'connected'; });
  var connectionState = anyConnected ? 'connected' : (Object.keys(peersUi).some(function(k) { return peersUi[k].state === 'failed'; }) && !Object.keys(peersUi).some(function(k) { return peersUi[k].state === 'connecting'; }) ? 'failed' : 'connecting');

  var onPillDown = function(e) {
    var evt = e.touches ? e.touches[0] : e;
    var rect = e.currentTarget.getBoundingClientRect();
    pillDragRef.current = { active: true, moved: false, offX: evt.clientX - rect.left, offY: evt.clientY - rect.top, w: rect.width, h: rect.height };
    var onMove = function(ev) {
      var d = pillDragRef.current;
      if (!d.active) return;
      d.moved = true;
      var m = ev.touches ? ev.touches[0] : ev;
      var vw = window.innerWidth, vh = window.innerHeight;
      setPillPos({ x: Math.max(4, Math.min(m.clientX - d.offX, vw - d.w - 4)), y: Math.max(4, Math.min(m.clientY - d.offY, vh - d.h - 4)) });
      if (ev.cancelable && ev.type === 'touchmove') ev.preventDefault();
    };
    var onUp = function() {
      var d = pillDragRef.current;
      d.active = false;
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp);
      if (d.moved) setPillPos(function(p) { try { localStorage.setItem('bbc:callPillPos', JSON.stringify(p)); } catch (e) { /* quota */ } return p; });
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onUp);
  };

  var localVideoRef = useRef(null);
  var localStreamRef = useRef(null);
  var screenStreamRef = useRef(null);
  var meshFallbackRef = useRef(false);
  var peersRef = useRef({});          // userId -> { pc, polite, makingOffer, remoteSet, iceBuffer }
  var mediaPromiseRef = useRef(null);
  var callContainerRef = useRef(null);
  var qualityIntervalRef = useRef(null);
  var cleanedUpRef = useRef(false);
  var statsAccRef = useRef({ kbps: 0, loss: 0, n: 0, prevBytes: 0, prevAt: 0 });
  var largeGroupVideoOffRef = useRef(false);

  // ==================== ICE SERVERS ====================
  var iceServers = useRef({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }], iceCandidatePoolSize: 10 });
  var iceServersReady = useRef(null);
  var p2pQualityRef = useRef('data_saver');   // Admin setting: 'data_saver' (540p / ~700 kbps / DTX) or 'quality' (720p / 1.5 Mbps)
  var fetchIceServers = useCallback(function() {
    if (iceServersReady.current) return iceServersReady.current;
    var token = auth && auth.token;
    iceServersReady.current = fetch(API + '/api/calls/ice-servers', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (d && d.p2p_quality) p2pQualityRef.current = d.p2p_quality === 'quality' ? 'quality' : 'data_saver';
        if (d && Array.isArray(d.iceServers) && d.iceServers.length) iceServers.current = { iceServers: d.iceServers, iceCandidatePoolSize: 10, iceTransportPolicy: d.iceTransportPolicy === 'relay' ? 'relay' : 'all' };
      })
      .catch(function(e) { console.warn('[VideoCall] ICE servers fetch failed, using STUN only', e); });
    return iceServersReady.current;
  }, [auth]);

  var videoConstraints = useCallback(function(extra) {
    var saver = p2pQualityRef.current !== 'quality';
    return Object.assign({ width: { ideal: saver ? 960 : 1280 }, height: { ideal: saver ? 540 : 720 }, frameRate: { ideal: saver ? 24 : 30, max: 30 } }, extra || {});
  }, []);

  // ==================== MEDIA ====================
  var ensureMedia = useCallback(function() {
    if (localStreamRef.current) return Promise.resolve(localStreamRef.current);
    if (mediaPromiseRef.current) return mediaPromiseRef.current;
    // Wait for the Admin quality setting (same request as the ICE servers) so the camera opens at the right size.
    mediaPromiseRef.current = fetchIceServers().catch(function() {}).then(function() {
      var constraints = {
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: callType === 'video' ? videoConstraints({ facingMode: 'user' }) : false
      };
      return navigator.mediaDevices.getUserMedia(constraints);
    }).then(function(stream) {
      localStreamRef.current = stream;
      stream.getVideoTracks().forEach(function(t) { try { t.contentHint = 'motion'; } catch (e) { /* noop */ } });
      stream.getAudioTracks().forEach(function(t) { try { t.contentHint = 'speech'; } catch (e) { /* noop */ } });
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      setMediaError(null);
      return stream;
    }).catch(function(error) {
      mediaPromiseRef.current = null;
      if (error.name === 'NotAllowedError') setMediaError('Camera/Microphone permission denied.');
      else if (error.name === 'NotFoundError') setMediaError('No camera or microphone found.');
      else setMediaError('Media error: ' + error.message);
      throw error;
    });
    return mediaPromiseRef.current;
  }, [callType, fetchIceServers, videoConstraints]);

  var updatePeerUi = useCallback(function(userId, patch) {
    setPeersUi(function(prev) {
      var cur = prev[userId] || { state: 'connecting', stream: null, name: '' };
      return Object.assign({}, prev, { [userId]: Object.assign({}, cur, patch) });
    });
  }, []);

  // Mesh calls multiply upload bandwidth by (participants - 1): cap video per
  // peer and keep audio at high priority so voices never break up.
  var applyEncodingCaps = useCallback(function() {
    // Mesh: our upload = (n peers) × one encoding. Budget ~1.5 Mbps total video on
    // mobile data; beyond 6 video peers we switch to audio-only automatically.
    var n = Object.keys(peersRef.current).length;
    var saver = p2pQualityRef.current !== 'quality';
    var maxBitrate = n <= 1 ? (saver ? 700000 : 1500000) : n <= 3 ? 450000 : n <= 6 ? 220000 : 0;
    var scaleDown = n <= 1 ? 1 : n <= 3 ? 2 : n <= 6 ? 4 : 4;
    var maxFps = n <= 1 ? (saver ? 24 : 30) : n <= 3 ? 20 : 15;
    var audioBitrate = saver ? 32000 : 48000;
    var audioOnly = n > 6;
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(function(t) {
        if (audioOnly && t.enabled) { t.enabled = false; largeGroupVideoOffRef.current = true; toast.info('Large group: camera switched off to keep audio clear'); }
        else if (!audioOnly && largeGroupVideoOffRef.current) { t.enabled = true; largeGroupVideoOffRef.current = false; }
      });
    }
    Object.keys(peersRef.current).forEach(function(k) {
      var pc = peersRef.current[k].pc;
      pc.getSenders().forEach(function(sender) {
        if (!sender.track) return;
        var params;
        try { params = sender.getParameters(); } catch (e) { return; }
        if (!params.encodings || !params.encodings.length) params.encodings = [{}];
        if (sender.track.kind === 'video') {
          params.encodings[0].active = !audioOnly;
          if (maxBitrate) params.encodings[0].maxBitrate = maxBitrate; else delete params.encodings[0].maxBitrate;
          params.encodings[0].scaleResolutionDownBy = scaleDown;
          params.encodings[0].maxFramerate = maxFps;
          params.encodings[0].priority = 'low';
          params.encodings[0].networkPriority = 'low';
          params.degradationPreference = 'maintain-framerate';
        } else {
          params.encodings[0].maxBitrate = audioBitrate;
          params.encodings[0].priority = 'high';
          params.encodings[0].networkPriority = 'high';
        }
        sender.setParameters(params).catch(function() {});
      });
    });
  }, []);

  // ==================== PEERS ====================
  var closePeer = useCallback(function(userId) {
    var p = peersRef.current[userId];
    if (p) { try { p.pc.onnegotiationneeded = null; p.pc.close(); } catch (e) { /* noop */ } delete peersRef.current[userId]; }
    setPeersUi(function(prev) { var n = Object.assign({}, prev); delete n[userId]; return n; });
    setTimeout(applyEncodingCaps, 0);
  }, [applyEncodingCaps]);

  var createPeer = useCallback(function(userId, name) {
    if (peersRef.current[userId]) return peersRef.current[userId];
    var pc = new RTCPeerConnection(iceServers.current);
    var peer = { pc: pc, polite: String(myId) > String(userId), makingOffer: false, remoteSet: false, iceBuffer: [] };
    peersRef.current[userId] = peer;
    updatePeerUi(userId, { state: 'connecting', name: name || '' });

    var stream = localStreamRef.current;
    if (stream) stream.getTracks().forEach(function(t) { pc.addTrack(t, stream); });

    pc.onicecandidate = function(ev) { if (ev.candidate) sendWebRTCSignal('ice-candidate', callId, userId, { candidate: ev.candidate.toJSON ? ev.candidate.toJSON() : ev.candidate }); };
    pc.ontrack = function(ev) {
      // One combined stream per peer: a screen-share track can arrive on a different stream id than the audio;
      // swapping the whole stream used to drop the remote audio (receiver went silent when sharing started).
      peer.remoteTracks = (peer.remoteTracks || []).filter(function(t) { return t.readyState !== 'ended' && t.id !== ev.track.id; });
      peer.remoteTracks.push(ev.track);
      var publish = function() {
        var live = peer.remoteTracks.filter(function(t) { return t.readyState !== 'ended'; });
        peer.remoteTracks = live;
        updatePeerUi(userId, { stream: new MediaStream(live) });
      };
      ev.track.onended = publish;
      ev.track.onunmute = publish;
      publish();
    };
    pc.onnegotiationneeded = function() {
      peer.makingOffer = true;
      pc.createOffer().then(function(offer) { return pc.setLocalDescription(withOpusDtx(offer)); }).then(function() {
        sendWebRTCSignal('offer', callId, userId, { sdp: pc.localDescription });
      }).catch(function(e) { console.error('[VideoCall] negotiation error', e); })
        .finally(function() { peer.makingOffer = false; });
    };
    pc.onconnectionstatechange = function() {
      var st = pc.connectionState;
      if (peer.freezeTimer) { clearTimeout(peer.freezeTimer); peer.freezeTimer = null; }
      if (st === 'connected') { updatePeerUi(userId, { state: 'connected' }); if (markConnected) markConnected(); applyEncodingCaps(); }
      else if (st === 'failed' || st === 'disconnected') {
        // Weak signal: keep retrying ICE restarts every 5 s for up to 90 s (the offer/answer also travel over the
        // REST fallback, so a dead WebSocket no longer blocks the reconnect). Only the caller side initiates
        // restarts to avoid offer glare; the callee waits for the new offer.
        updatePeerUi(userId, { state: st === 'failed' ? 'failed' : 'connecting' });
        var attempts = 0;
        var retry = function() {
          if (pc.connectionState === 'connected' || pc.connectionState === 'closed') { peer.freezeTimer = null; return; }
          if (attempts >= 8 && peer.polite === false && !meshFallbackRef.current) {
            // ~40 s without recovery on the direct path → move the whole call to the media server (both sides follow)
            meshFallbackRef.current = true;
            toast.warning('Direct connection keeps failing — switching to the media server');
            fetch(API + '/api/calls/' + callId + '/media-mode', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token }, body: JSON.stringify({ mode: 'livekit' }) })
              .then(function(r) { if (r.ok) chat.setMediaMode('livekit'); }).catch(function() {});
            peer.freezeTimer = null; return;
          }
          if (attempts >= 18) { peer.freezeTimer = null; return; }
          attempts += 1;
          if (peer.polite === false || attempts % 3 === 0) { try { pc.restartIce(); } catch (e) { /* noop */ } }
          peer.freezeTimer = setTimeout(retry, 5000);
        };
        peer.freezeTimer = setTimeout(retry, st === 'failed' ? 500 : 4000);
      }
      else if (st === 'closed') updatePeerUi(userId, { state: 'closed' });
    };
    applyEncodingCaps();
    return peer;
  }, [callId, myId, sendWebRTCSignal, updatePeerUi, markConnected, applyEncodingCaps]);

  var flushIce = function(peer) {
    var buf = peer.iceBuffer; peer.iceBuffer = [];
    buf.forEach(function(c) { peer.pc.addIceCandidate(new RTCIceCandidate(c)).catch(function() {}); });
  };

  // Joiner: send offers to everyone already in the call.
  useEffect(function() {
    var targets = callState.peersToOffer || [];
    if (!targets.length || !callId || callId === 'pending') return;
    clearPeersToOffer();
    Promise.all([ensureMedia(), fetchIceServers()]).then(function() {
      targets.forEach(function(t) { if (t.user_id && t.user_id !== myId) createPeer(t.user_id, t.name); });
    }).catch(function(e) { console.error('[VideoCall] could not start peers', e); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState.peersToOffer, callId]);

  // Keep names in sync with the participant list
  useEffect(function() {
    participants.forEach(function(p) { if (p.user_id !== myId && peersRef.current[p.user_id]) updatePeerUi(p.user_id, { name: p.name }); });
  }, [participants, myId, updatePeerUi]);

  // Pre-warm local media + ICE as soon as the call UI opens (caller waits for the joiner's offer).
  useEffect(function() {
    Promise.all([ensureMedia(), fetchIceServers()]).catch(function() {});
  }, [ensureMedia, fetchIceServers]);

  // ==================== SIGNALLING ====================
  var handleWebRTCSignal = useCallback(function(data) {
    if (data.call_id && callId && data.call_id !== callId) return;
    var from = data.sender_id; var type = data.signal_type; var payload = data.payload || {};
    if (!from || from === myId) return;
    var peer = peersRef.current[from];

    if (type === 'offer') {
      Promise.all([ensureMedia(), fetchIceServers()]).then(function() {
        peer = peer || createPeer(from, (participants.find(function(p) { return p.user_id === from; }) || {}).name);
        var pc = peer.pc;
        var sdp = payload.sdp && payload.sdp.sdp;
        // Signals travel twice on purpose (WebSocket + REST queue): apply each offer only once.
        if (sdp && (peer.lastOfferSdp === sdp || (pc.remoteDescription && pc.remoteDescription.sdp === sdp))) return null;
        var collision = peer.makingOffer || pc.signalingState !== 'stable';
        if (!peer.polite && collision) return null; // impolite side ignores the colliding offer
        peer.lastOfferSdp = sdp;
        return pc.setRemoteDescription(payload.sdp).then(function() {
          peer.remoteSet = true; flushIce(peer);
          return pc.createAnswer().then(function(answer) { return pc.setLocalDescription(withOpusDtx(answer)); });
        }).then(function() { sendWebRTCSignal('answer', callId, from, { sdp: pc.localDescription }); });
      }).catch(function(e) { console.error('[VideoCall] offer handling error', e); });
    } else if (type === 'answer') {
      if (!peer) return;
      var asdp = payload.sdp && payload.sdp.sdp;
      if (peer.pc.signalingState !== 'have-local-offer' || (asdp && peer.lastAnswerSdp === asdp)) return; // duplicate / late answer
      peer.lastAnswerSdp = asdp;
      peer.pc.setRemoteDescription(payload.sdp).then(function() { peer.remoteSet = true; flushIce(peer); })
        .catch(function(e) { console.error('[VideoCall] answer error', e); });
    } else if (type === 'ice-candidate') {
      if (!peer) { peer = createPeer(from); }
      var c = payload.candidate;
      if (!c) return;
      if (peer.remoteSet) peer.pc.addIceCandidate(new RTCIceCandidate(c)).catch(function() {});
      else peer.iceBuffer.push(c);
    }
  }, [callId, myId, participants, ensureMedia, fetchIceServers, createPeer, sendWebRTCSignal]);

  // ==================== CLEANUP ====================
  // The self-preview tile only mounts for video calls / while sharing: (re)attach the right stream once it exists.
  useEffect(function() {
    var el = localVideoRef.current; if (!el) return;
    var want = screenStreamRef.current || localStreamRef.current;
    if (want && el.srcObject !== want) el.srcObject = want;
  }, [isScreenSharing, callType, connectionState]);

  var cleanupMedia = useCallback(function() {
    if (cleanedUpRef.current) return;
    cleanedUpRef.current = true;
    var a = statsAccRef.current;
    if (a.n > 0 && callId && callId !== 'pending' && auth && auth.token) {
      fetch(API + '/api/calls/' + callId + '/stats', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
        body: JSON.stringify({ mode: 'mesh', avg_kbps: a.kbps / a.n, avg_loss_pct: a.loss / a.n, samples: a.n }), keepalive: true }).catch(function() {});
    }
    if (qualityIntervalRef.current) { clearInterval(qualityIntervalRef.current); qualityIntervalRef.current = null; }
    Object.keys(peersRef.current).forEach(function(k) { try { peersRef.current[k].pc.close(); } catch (e) { /* noop */ } });
    peersRef.current = {};
    [localStreamRef, screenStreamRef].forEach(function(r) { if (r.current) { r.current.getTracks().forEach(function(t) { t.stop(); }); r.current = null; } });
    if (window.bbcScreenFrame) { window.bbcScreenFrame = null; try { window.AndroidBridge && window.AndroidBridge.stopScreenCapture && window.AndroidBridge.stopScreenCapture(); } catch (e) { /* noop */ } }
    mediaPromiseRef.current = null;
  }, [callId, auth]);

  useEffect(function() {
    cleanedUpRef.current = false;
    var unreg1 = registerHandler('webrtcSignal', handleWebRTCSignal);
    var unreg2 = registerHandler('callAccepted', function() { /* joiner initiates; nothing to do */ });
    var unreg3 = registerHandler('callEnded', function() { cleanupMedia(); });
    var unreg4 = registerHandler('participantLeft', function(d) { if (d && d.user_id) { closePeer(d.user_id); toast.info((d.name || 'A participant') + ' left the call'); } });
    return function() { unreg1(); unreg2(); unreg3(); unreg4(); };
  }, [handleWebRTCSignal, registerHandler, cleanupMedia, closePeer]);

  useEffect(function() { return cleanupMedia; }, [cleanupMedia]);

  // Everyone else left → end for me too
  useEffect(function() {
    if (callState.leftPeer && participants.length <= 1 && callState.phase === 'connected') {
      toast.info('Everyone has left the call');
      cleanupMedia(); endCall('hung_up');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState.leftAt]);

  // ==================== CONTROLS ====================
  useEffect(function() {
    var h = function(e) { var d = e.detail || {}; if (d.user_id !== (auth.user && auth.user.user_id)) return; var st = localStreamRef.current; if (!st) return;
      st.getAudioTracks().forEach(function(t) { t.enabled = !d.muted; }); setIsAudioEnabled(!d.muted); toast.info((d.by_name || 'The host') + (d.muted ? ' muted' : ' unmuted') + ' your microphone'); };
    window.addEventListener('bbc:force-mute', h); return function() { window.removeEventListener('bbc:force-mute', h); };
  }, [auth.user]); // eslint-disable-line react-hooks/exhaustive-deps
  var toggleAudio = function() { var s = localStreamRef.current; if (!s) return; s.getAudioTracks().forEach(function(t) { t.enabled = !t.enabled; }); setIsAudioEnabled(!isAudioEnabled); };
  var facingRef = useRef('user');
  var _route = useState({ routes: [], current: 'auto', open: false }); var audioRoute = _route[0], setAudioRoute = _route[1];
  var _pip = useState(false); var pipActive = _pip[0], setPipActive = _pip[1];
  var nativeAudio = function() { var b = window.AndroidBridge; return b && typeof b.setAudioRoute === 'function' ? b : null; };

  // Camera on/off — in a voice call the first "on" adds a camera track to every peer (renegotiation).
  var toggleVideo = function() {
    var s = localStreamRef.current; if (!s) return;
    var vts = s.getVideoTracks();
    if (vts.length) { vts.forEach(function(t) { t.enabled = !t.enabled; }); setIsVideoEnabled(!isVideoEnabled); return; }
    navigator.mediaDevices.getUserMedia({ video: videoConstraints({ facingMode: facingRef.current }) }).then(function(vs) {
      var track = vs.getVideoTracks()[0]; s.addTrack(track);
      forEachPc(function(pc) {
        var sender = pc.getSenders().find(function(x) { return x.track && x.track.kind === 'video'; });
        if (sender) sender.replaceTrack(track).catch(function() {}); else pc.addTrack(track, s);
      });
      if (localVideoRef.current) localVideoRef.current.srcObject = s;
      setIsVideoEnabled(true);
    }).catch(function() { toast.error('Camera not available'); });
  };
  // Flip front/back camera: swap the sender's track in place (no renegotiation needed).
  var flipCamera = function() {
    var s = localStreamRef.current; if (!s || !s.getVideoTracks().length || isScreenSharing) return;
    var next = facingRef.current === 'user' ? 'environment' : 'user';
    navigator.mediaDevices.getUserMedia({ video: videoConstraints({ facingMode: { exact: next } }) })
      .catch(function() { return navigator.mediaDevices.getUserMedia({ video: { facingMode: next } }); })
      .then(function(vs) {
        var track = vs.getVideoTracks()[0]; var old = s.getVideoTracks()[0];
        forEachPc(function(pc) { var sender = pc.getSenders().find(function(x) { return x.track && x.track.kind === 'video'; }); if (sender) sender.replaceTrack(track).catch(function() {}); });
        if (old) { s.removeTrack(old); old.stop(); }
        s.addTrack(track); facingRef.current = next;
        if (localVideoRef.current) localVideoRef.current.srcObject = s;
      }).catch(function() { toast.error('Could not switch camera'); });
  };
  // Sound output: BBC app → native routes (Auto = proximity + Bluetooth); browsers → setSinkId on the audio elements.
  var loadAudioRoutes = function() {
    var b = nativeAudio();
    if (b) {
      try { var r = JSON.parse(b.getAudioRoutes() || '{}'); setAudioRoute(function(prev) { return Object.assign({}, prev, { routes: r.routes || [], current: r.current || 'auto' }); }); } catch (e) { /* noop */ }
      return;
    }
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices && typeof HTMLMediaElement.prototype.setSinkId === 'function') {
      navigator.mediaDevices.enumerateDevices().then(function(devs) {
        var outs = devs.filter(function(d) { return d.kind === 'audiooutput'; }).map(function(d, i) { return { id: d.deviceId, label: d.label || ('Output ' + (i + 1)) }; });
        setAudioRoute(function(prev) { return Object.assign({}, prev, { routes: outs }); });
      });
    }
  };
  var chooseAudioRoute = function(id) {
    var b = nativeAudio();
    if (b) { try { b.setAudioRoute(id); } catch (e) { /* noop */ } }
    else { document.querySelectorAll('audio[data-peer-audio]').forEach(function(el) { if (el.setSinkId) el.setSinkId(id).catch(function() {}); }); }
    setAudioRoute(function(prev) { return Object.assign({}, prev, { current: id, open: false }); });
  };
  useEffect(function() {
    var onRoute = function(ev) { var d = (ev && ev.detail) || {}; if (d.current) setAudioRoute(function(prev) { return Object.assign({}, prev, { current: d.current, routes: d.routes || prev.routes }); }); };
    var onPip = function(ev) { setPipActive(!!(ev && ev.detail && ev.detail.active)); };
    window.addEventListener('bbc:audio-route', onRoute); window.addEventListener('bbc:pip', onPip);
    if (connectionState === 'connected') loadAudioRoutes();
    return function() { window.removeEventListener('bbc:audio-route', onRoute); window.removeEventListener('bbc:pip', onPip); };
  }, [connectionState]); // eslint-disable-line react-hooks/exhaustive-deps
  var canPip = !!(window.AndroidBridge && typeof window.AndroidBridge.enterPip === 'function');
  var enterPip = function() { try { window.AndroidBridge.enterPip(); } catch (e) { /* noop */ } };

  var forEachPc = function(fn) { Object.keys(peersRef.current).forEach(function(k) { fn(peersRef.current[k].pc, k); }); };

  // Android/iOS cut the mic/camera when the screen locks or the app goes to the
  // background and leave the tracks muted/ended afterwards. When the page is
  // visible again, silently re-acquire them and swap them into every peer.
  var restoreLocalTracks = useCallback(function() {
    setTimeout(function() {
      var st = localStreamRef.current;
      if (!st || cleanedUpRef.current) return;
      var dead = function(t) { return t.readyState === 'ended' || t.muted; };
      var needAudio = st.getAudioTracks().some(dead);
      var needVideo = callType === 'video' && !screenStreamRef.current && st.getVideoTracks().some(dead);
      if (!needAudio && !needVideo) return;
      var constraints = {
        audio: needAudio ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true } : false,
        video: needVideo ? videoConstraints({ facingMode: 'user' }) : false,
      };
      navigator.mediaDevices.getUserMedia(constraints).then(function(fresh) {
        fresh.getTracks().forEach(function(nt) {
          var old = (nt.kind === 'audio' ? st.getAudioTracks() : st.getVideoTracks())[0];
          nt.enabled = old ? old.enabled : true;
          Object.keys(peersRef.current).forEach(function(k) {
            var pc = peersRef.current[k].pc;
            var sender = pc.getSenders().find(function(s) { return old && s.track === old; })
              || pc.getSenders().find(function(s) { return s.track && s.track.kind === nt.kind; });
            if (sender) sender.replaceTrack(nt).catch(function() {});
          });
          if (old) { try { old.stop(); } catch (e) { /* noop */ } st.removeTrack(old); }
          st.addTrack(nt);
        });
        if (localVideoRef.current && !screenStreamRef.current) localVideoRef.current.srcObject = st;
        toast.success(needAudio ? 'Microphone restored' : 'Camera restored');
      }).catch(function(e) { console.warn('[VideoCall] track restore failed', e); });
    }, 1200);
  }, [callType, videoConstraints]);
  useCallKeepAlive(callState.phase === 'connected' || callState.phase === 'outgoing_ringing' || callState.phase === 'connecting', restoreLocalTracks,
    { title: recipientName ? (callType === 'video' ? 'Video call with ' : 'Call with ') + recipientName : '', video: callType === 'video' });

  // BBC Android app: the WebView cannot capture the screen itself (no getDisplayMedia). The app captures it natively
  // (MediaProjection) and streams JPEG frames into a canvas here; canvas.captureStream() becomes the WebRTC video
  // track, so the share still travels phone-to-phone on the direct/relay path like camera video.
  var nativeShareRef = useRef(null);
  var nativeShareBridge = function() {
    var b = window.AndroidBridge;
    return b && typeof b.startScreenCapture === 'function' && typeof b.stopScreenCapture === 'function' ? b : null;
  };
  var stopNativeShare = function() {
    var ns = nativeShareRef.current; if (!ns) return;
    nativeShareRef.current = null;
    window.bbcScreenFrame = null;
    try { nativeShareBridge() && nativeShareBridge().stopScreenCapture(); } catch (e) { /* noop */ }
    if (ns.img) ns.img.onload = null;
  };
  var startNativeShare = function() {
    var bridge = nativeShareBridge();
    var canvas = document.createElement('canvas');
    canvas.width = 540; canvas.height = 1170;
    var ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111827'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    var stream = canvas.captureStream(8);
    var img = new Image();
    var ns = { canvas: canvas, ctx: ctx, img: img, frames: 0, started: false };
    nativeShareRef.current = ns;
    window.bbcScreenFrame = function(dataUrl) {
      if (nativeShareRef.current !== ns) return;
      img.onload = function() {
        if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) { canvas.width = img.naturalWidth; canvas.height = img.naturalHeight; }
        ctx.drawImage(img, 0, 0);
        ns.frames += 1;
      };
      img.src = dataUrl;
    };
    var onEvent = function(ev) {
      var d = (ev && ev.detail) || {};
      if (d.kind === 'started') { ns.started = true; return; }
      if (d.kind === 'denied') { toast.error('Screen sharing permission was not granted'); window.removeEventListener('bbc:screen-capture', onEvent); stopNativeShare(); stopScreenShare(); return; }
      if (d.kind === 'stopped') { window.removeEventListener('bbc:screen-capture', onEvent); if (nativeShareRef.current === ns) { stopNativeShare(); stopScreenShare(); } }
    };
    window.addEventListener('bbc:screen-capture', onEvent);
    ns.offEvent = function() { window.removeEventListener('bbc:screen-capture', onEvent); };
    try { bridge.startScreenCapture(); } catch (e) { toast.error('Could not start screen sharing'); stopNativeShare(); return null; }
    return stream;
  };

  var stopScreenShare = useCallback(function() {
    var ss = screenStreamRef.current; if (!ss) return;
    if (nativeShareRef.current) { if (nativeShareRef.current.offEvent) nativeShareRef.current.offEvent(); stopNativeShare(); }
    var screenTrack = ss.getVideoTracks()[0];
    var camTrack = localStreamRef.current ? localStreamRef.current.getVideoTracks()[0] : null;
    forEachPc(function(pc) {
      var sender = pc.getSenders().find(function(s) { return s.track === screenTrack || (s.track && s.track.kind === 'video'); });
      if (!sender) return;
      if (camTrack) sender.replaceTrack(camTrack).catch(function() {});
      else { try { pc.removeTrack(sender); } catch (e) { /* triggers renegotiation */ } }
    });
    ss.getTracks().forEach(function(t) { t.stop(); });
    screenStreamRef.current = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    setIsScreenSharing(false);
    setIsVideoEnabled(!!camTrack && camTrack.enabled);
  }, []);

  var toggleScreenShare = function() {
    if (isScreenSharing) { stopScreenShare(); return; }
    var hasDisplayMedia = navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;
    if (!hasDisplayMedia && nativeShareBridge()) {
      var nativeStream = startNativeShare();
      if (!nativeStream) return;
      screenStreamRef.current = nativeStream;
      var nTrack = nativeStream.getVideoTracks()[0];
      try { nTrack.contentHint = 'detail'; } catch (e) { /* noop */ }
      forEachPc(function(pc) {
        var vs = pc.getSenders().find(function(s) { return s.track && s.track.kind === 'video'; });
        if (vs) vs.replaceTrack(nTrack).catch(function() {});
        else pc.addTrack(nTrack, localStreamRef.current || nativeStream);
      });
      if (localVideoRef.current) localVideoRef.current.srcObject = nativeStream;
      setIsScreenSharing(true);
      setIsVideoEnabled(true);
      return;
    }
    if (!hasDisplayMedia) {
      toast.error('Screen sharing is not supported on this device', { description: 'Use the app in a desktop browser (Chrome/Edge) to share your screen.' });
      return;
    }
    navigator.mediaDevices.getDisplayMedia({ video: { cursor: 'always' }, audio: false }).then(function(screenStream) {
      screenStreamRef.current = screenStream;
      var screenTrack = screenStream.getVideoTracks()[0];
      forEachPc(function(pc) {
        var vs = pc.getSenders().find(function(s) { return s.track && s.track.kind === 'video'; });
        if (vs) vs.replaceTrack(screenTrack).catch(function() {});
        else pc.addTrack(screenTrack, localStreamRef.current || screenStream);   // voice call: adds video m-line → onnegotiationneeded → re-offer
      });
      if (localVideoRef.current) localVideoRef.current.srcObject = screenStream;
      screenTrack.onended = function() { stopScreenShare(); };
      setIsScreenSharing(true);
      setIsVideoEnabled(true);
    }).catch(function(e) { if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') { console.error('Screen share error:', e); toast.error('Could not start screen sharing'); } });
  };

  var toggleFullscreen = function() {
    if (!isFullscreen) { if (callContainerRef.current && callContainerRef.current.requestFullscreen) callContainerRef.current.requestFullscreen(); }
    else if (document.exitFullscreen) { document.exitFullscreen(); }
    setIsFullscreen(!isFullscreen);
  };

  var handleEndCall = useCallback(function() { cleanupMedia(); endCall('hung_up'); }, [cleanupMedia, endCall]);

  // ==================== ADD PARTICIPANTS ====================
  useEffect(function() {
    if (!addOpen) return undefined;
    var t = setTimeout(function() {
      var q = addQuery.trim();
      fetch(API + '/api/chat/users/search?q=' + encodeURIComponent(q || 'a') + '&limit=30', { headers: { Authorization: 'Bearer ' + (auth && auth.token) } })
        .then(function(r) { return r.ok ? r.json() : []; })
        .then(function(d) {
          var list = Array.isArray(d) ? d : (d.users || d.results || []);
          var inCall = {}; participants.forEach(function(p) { inCall[p.user_id] = true; }); inCall[myId] = true;
          setAddResults(list.filter(function(u) { return u.user_id && !inCall[u.user_id] && (u.user_type || u.role) !== 'customer'; }).slice(0, 20));
        }).catch(function() { setAddResults([]); });
    }, 300);
    return function() { clearTimeout(t); };
  }, [addOpen, addQuery, auth, participants, myId]);

  var invite = function(u) {
    inviteToCall([u.user_id]);
    toast.success('Ringing ' + (u.name || 'participant') + '…');
    setAddOpen(false); setAddQuery('');
  };

  // ==================== CALL QUALITY ====================
  var monitorCallQuality = useCallback(function() {
    var pcs = Object.keys(peersRef.current).map(function(k) { return peersRef.current[k].pc; });
    if (!pcs.length) return;
    Promise.all(pcs.map(function(pc) { return pc.getStats().catch(function() { return null; }); })).then(function(all) {
      var totalRtt = 0, rttCount = 0, lost = 0, received = 0, jitterSum = 0, jitterCount = 0, relayed = false, direct = false, bytesOut = 0;
      all.forEach(function(stats) {
        if (!stats) return;
        var byId = {}; stats.forEach(function(r) { byId[r.id] = r; });
        stats.forEach(function(r) {
          if (r.type === 'outbound-rtp' && r.bytesSent) bytesOut += r.bytesSent;
          if (r.type === 'candidate-pair' && (r.state === 'succeeded' || r.nominated) && r.currentRoundTripTime !== undefined) {
            totalRtt += r.currentRoundTripTime; rttCount++;
            var lc = byId[r.localCandidateId], rc = byId[r.remoteCandidateId];
            if ((lc && lc.candidateType === 'relay') || (rc && rc.candidateType === 'relay')) relayed = true; else direct = true;
          }
          if (r.type === 'inbound-rtp') { if (r.packetsLost) lost += r.packetsLost; if (r.packetsReceived) received += r.packetsReceived; if (r.jitter) { jitterSum += r.jitter; jitterCount++; } }
        });
      });
      var latency = rttCount > 0 ? Math.round((totalRtt / rttCount) * 1000) : 0;
      var total = lost + received;
      var packetLoss = total > 0 ? Math.round((lost / total) * 1000) / 10 : 0;
      var acc = statsAccRef.current, nowMs = Date.now();
      if (acc.prevAt && bytesOut > acc.prevBytes) { acc.kbps += Math.round(((bytesOut - acc.prevBytes) * 8) / ((nowMs - acc.prevAt) / 1000) / 1000); acc.loss += packetLoss; acc.n += 1; }
      acc.prevBytes = bytesOut; acc.prevAt = nowMs;
      var jitter = jitterCount > 0 ? Math.round((jitterSum / jitterCount) * 1000) : 0;
      var level = 'excellent';
      if (latency > 300 || packetLoss > 5 || jitter > 50) level = 'poor';
      else if (latency > 200 || packetLoss > 2 || jitter > 30) level = 'fair';
      else if (latency > 100 || packetLoss > 0.5 || jitter > 15) level = 'good';
      setCallQuality({ level: level, latency: latency, packetLoss: packetLoss, jitter: jitter, relayed: relayed ? true : (direct ? false : null) });
    });
  }, []);

  useEffect(function() {
    if (connectionState === 'connected') {
      qualityIntervalRef.current = setInterval(monitorCallQuality, 3000);
      monitorCallQuality();
    }
    return function() { if (qualityIntervalRef.current) { clearInterval(qualityIntervalRef.current); qualityIntervalRef.current = null; } };
  }, [connectionState, monitorCallQuality]);

  useEffect(function() {
    if (connectionState !== 'connected') return undefined;
    var t = setInterval(function() { setCallDuration(function(d) { return d + 1; }); }, 1000);
    return function() { clearInterval(t); };
  }, [connectionState]);

  var getQualityIndicator = function() {
    switch (callQuality.level) {
      case 'excellent': return { icon: SignalHigh, color: 'text-green-400', label: 'Excellent' };
      case 'good': return { icon: SignalMedium, color: 'text-green-300', label: 'Good' };
      case 'fair': return { icon: SignalLow, color: 'text-yellow-400', label: 'Fair' };
      case 'poor': return { icon: WifiOff, color: 'text-red-400', label: 'Poor' };
      default: return { icon: Wifi, color: 'text-gray-400', label: 'Checking...' };
    }
  };
  var formatDuration = function(s) { return Math.floor(s / 60).toString().padStart(2, '0') + ':' + (s % 60).toString().padStart(2, '0'); };

  // ==================== RENDER ====================
  var QIcon = getQualityIndicator().icon;
  var peerIds = Object.keys(peersUi).filter(function(k) { return peersUi[k].state !== 'closed'; });
  var gridCols = peerIds.length <= 1 ? 'grid-cols-1' : peerIds.length <= 4 ? 'grid-cols-2' : 'grid-cols-3';
  var showVideoTiles = callType === 'video' || isScreenSharing || peerIds.some(function(k) { var s = peersUi[k].stream; return s && s.getVideoTracks().length > 0; });

  return (
    <>
      {minimized && (
        <div className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 pl-2 pr-2 py-2 rounded-full bg-gray-900 text-white shadow-2xl border border-white/10 cursor-grab active:cursor-grabbing touch-none select-none animate-in fade-in zoom-in-95"
          data-testid="call-pill" onMouseDown={onPillDown} onTouchStart={onPillDown}
          style={pillPos ? { left: pillPos.x, top: pillPos.y, right: 'auto', bottom: 'auto' } : undefined}>
          <button type="button" onClick={function() { if (!pillDragRef.current.moved) setMinimized(false); }} className="flex items-center gap-2 pl-1" data-testid="call-pill-restore">
            <span className="relative flex w-9 h-9 rounded-full bg-green-600 items-center justify-center">
              {callType === 'video' ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
              {connectionState === 'connected' && <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50 animate-ping" />}
            </span>
            <span className="text-left leading-tight pr-1">
              <span className="block text-xs font-medium">{connectionState === 'connected' ? 'Ongoing call' + (peerIds.length > 1 ? ' · ' + (peerIds.length + 1) : '') : 'Connecting…'}</span>
              <span className="block text-[11px] font-mono text-green-300">{connectionState === 'connected' ? formatDuration(callDuration) : 'Connecting…'}</span>
            </span>
          </button>
          <Button variant="ghost" size="icon" onClick={handleEndCall} className="w-9 h-9 rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="call-pill-end"><PhoneOff className="w-4 h-4" /></Button>
        </div>
      )}
      <div ref={callContainerRef} style={{ display: minimized ? 'none' : 'flex' }} className={'fixed inset-0 bg-gray-900 z-50 flex-col' + (pipActive ? ' bbc-pip' : '')} data-testid="video-call" data-pip={pipActive ? '1' : '0'}>
        {pipActive && <style>{'.bbc-pip .absolute.top-0, .bbc-pip .absolute.bottom-0 { display: none !important; } .bbc-pip [data-testid="local-video"] { display: none !important; }'}</style>}
        {/* Remote audio for every peer (voice calls / hidden tiles) */}
        {peerIds.map(function(k) { return <PeerAudio key={'a' + k} stream={peersUi[k].stream} />; })}

        <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Badge variant={connectionState === 'connected' ? 'default' : 'secondary'} data-testid="call-connection-badge">
              {connectionState === 'connected' ? 'Connected' : connectionState === 'failed' ? 'Failed' : 'Connecting...'}
            </Badge>
            {connectionState === 'connected' && callQuality.relayed !== null && (
              <Badge variant="outline" className={'border-white/30 ' + (callQuality.relayed ? 'text-amber-300' : 'text-green-300')} data-testid="call-path-badge">
                {callQuality.relayed ? 'Relayed' : 'Direct'}
              </Badge>
            )}
            {connectionState === 'connected' ? <span className="text-white font-mono text-sm" data-testid="call-timer">{formatDuration(callDuration)}</span> : <span className="text-white/70 text-xs animate-pulse" data-testid="call-connecting">{connectionState === 'failed' ? 'Failed' : 'Connecting…'}</span>}
            {peerIds.length > 0 && <span className="text-white/70 text-xs hidden sm:inline" data-testid="call-participant-count">{peerIds.length + 1} in call</span>}
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {connectionState === 'connected' && (
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={function() { setShowQualityDetails(!showQualityDetails); }}
                  className={'text-white hover:bg-white/20 gap-1 px-2 ' + getQualityIndicator().color} data-testid="call-quality-btn">
                  <QIcon className="w-5 h-5" /><span className="text-xs hidden sm:inline">{getQualityIndicator().label}</span>
                </Button>
                {showQualityDetails && (
                  <div className="absolute right-0 top-full mt-1 bg-gray-800/95 backdrop-blur rounded-lg p-3 min-w-[210px] text-white text-xs space-y-1 shadow-xl border border-gray-700" data-testid="call-quality-details">
                    <div className="font-semibold text-sm mb-2">Quality: {getQualityIndicator().label}</div>
                    <div className="flex justify-between"><span className="text-gray-400">Path:</span><span>{callQuality.relayed === null ? '—' : callQuality.relayed ? 'Relayed via TURN' : 'Direct (peer-to-peer)'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Latency:</span><span>{callQuality.latency}ms</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Packet Loss:</span><span>{callQuality.packetLoss}%</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Jitter:</span><span>{callQuality.jitter}ms</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Mode:</span><span>Direct connection</span></div>
                    <button type="button" data-testid="switch-server-btn" className="mt-2 w-full rounded-md bg-white/15 hover:bg-white/25 px-2 py-1.5 text-left"
                      onClick={function() {
                        fetch(API + '/api/calls/' + callId + '/media-mode', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token }, body: JSON.stringify({ mode: 'livekit' }) })
                          .then(function(r) { if (!r.ok) throw new Error('Failed'); toast.success('Switching to media server…'); setShowQualityDetails(false); chat.setMediaMode('livekit'); })
                          .catch(function(e) { toast.error(e.message); });
                      }}>
                      <span className="block font-medium">Use media server</span>
                      <span className="block text-[10px] text-gray-300">More stable on mobile data and weak signal.</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={function() { setNotesOpen(!notesOpen); }} className={'text-white hover:bg-white/20 relative ' + (notesOpen ? 'bg-white/20' : '')} title="Meeting notes" data-testid="meeting-notes-btn"><NotebookPen className="w-5 h-5" />{aiOn && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-testid="ai-notes-dot" />}</Button>
            <Button variant="ghost" size="icon" onClick={function() { setAddOpen(true); }} className="text-white hover:bg-white/20" title="Add participant" data-testid="add-participant-btn"><UserPlus className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={function() { setMinimized(true); }} className="text-white hover:bg-white/20" data-testid="minimise-call-btn"><Minus className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-white hover:bg-white/20 hidden sm:inline-flex" data-testid="fullscreen-btn">{isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</Button>
          </div>
        </div>

        <CallExtras callId={callId} token={auth.token} isInitiator={!!callState.isInitiator} isAdmin={auth.user && auth.user.role === 'admin'} myId={auth.user && auth.user.user_id}
          members={participants.map(function(p) { return { user_id: p.user_id, name: p.name }; })} />
        <MeetingNotesPanel callId={callId} token={auth.token} isHost={!!callState.isInitiator || (auth.user && auth.user.role === 'admin')} open={notesOpen} onClose={function() { setNotesOpen(false); }} onStatus={setAiOn}
          getMicTrack={function() { var st = localStreamRef.current; return st ? st.getAudioTracks()[0] : null; }} />
        {mediaError && <div className="absolute top-16 left-4 right-4 z-20 bg-red-600/90 text-white p-3 rounded-lg text-sm">{mediaError}</div>}

        <div className="flex-1 relative overflow-hidden pt-14 pb-24">
          {showVideoTiles ? (
            <div className={'grid gap-1 h-full w-full p-1 ' + gridCols} data-testid="remote-video-grid">
              {peerIds.map(function(k) { return <PeerTile key={k} userId={k} info={peersUi[k]} />; })}
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
              <div className="text-center">
                <div className={'w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ' + (connectionState === 'connected' ? 'bg-green-600' : 'bg-gray-700')}><Mic className="w-10 h-10 text-white" /></div>
                <p className="text-white text-lg font-medium">{connectionState === 'connected' ? 'Voice Call Connected' : 'Voice Call...'}</p>
                <p className="text-gray-300 text-sm mt-1">{peerIds.map(function(k) { return peersUi[k].name || 'Participant'; }).join(', ')}</p>
                <p className="text-gray-400 text-sm">{connectionState === 'connected' ? formatDuration(callDuration) : 'Connecting…'}</p>
              </div>
            </div>
          )}
          {connectionState !== 'connected' && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 pointer-events-none">
              <div className="text-center text-white">
                <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
                <p className="text-lg font-medium">{connectionState === 'failed' ? 'Connection Failed' : callState.phase === 'outgoing_ringing' ? ('Calling ' + (recipientName || '') + '…') : ('Connecting' + (recipientName ? ' to ' + recipientName : '') + '...')}</p>
                {callState.statusNote && <p className="text-amber-300 text-sm font-medium mt-1" data-testid="call-status-hint">{callState.statusNote} — still ringing</p>}
              </div>
            </div>
          )}
          {(callType === 'video' || isScreenSharing) && (
            <div className="absolute bottom-28 right-4 w-28 h-20 sm:w-48 sm:h-36 rounded-lg overflow-hidden shadow-xl border-2 border-white/20 z-10">
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: isScreenSharing ? 'none' : 'scaleX(-1)' }} data-testid="local-video" />
            </div>
          )}
        </div>

        {addOpen && (
          <div className="absolute inset-0 z-30 bg-black/70 flex items-end sm:items-center justify-center p-3" data-testid="add-participant-dialog">
            <div className="bg-white rounded-xl w-full max-w-md p-4 max-h-[75vh] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-base">Add to call</h3>
                <Button variant="ghost" size="icon" onClick={function() { setAddOpen(false); }} data-testid="add-participant-close"><X className="w-4 h-4" /></Button>
              </div>
              <Input autoFocus placeholder="Search staff by name…" value={addQuery} onChange={function(e) { setAddQuery(e.target.value); }} data-testid="add-participant-search" />
              <div className="mt-2 overflow-y-auto divide-y">
                {addResults.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No staff found</p>}
                {addResults.map(function(u) {
                  return (
                    <button key={u.user_id} type="button" onClick={function() { invite(u); }} className="w-full text-left py-2.5 px-1 hover:bg-muted rounded flex items-center justify-between" data-testid={'add-participant-' + u.user_id}>
                      <span className="min-w-0"><span className="block text-sm font-medium truncate">{u.name}</span><span className="block text-xs text-muted-foreground truncate">{u.role || u.email || ''}</span></span>
                      <Phone className="w-4 h-4 text-green-600 shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex flex-col items-center gap-2 bg-gradient-to-t from-black/70 to-transparent">
          {IS_TOUCH_DEVICE && <p className="text-[11px] text-white/70 text-center max-w-xs" data-testid="lock-screen-hint">{LOCK_HINT}</p>}
          <div className="flex items-center justify-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={toggleAudio} className={'w-12 h-12 rounded-full ' + (isAudioEnabled ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-red-500 text-white hover:bg-red-600')} data-testid="toggle-audio">{isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</Button>
          {(
            <Button variant="ghost" size="icon" onClick={toggleVideo} className={'w-12 h-12 rounded-full ' + (isVideoEnabled ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-red-500 text-white hover:bg-red-600')} data-testid="toggle-video">{isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}</Button>
          )}
          {isVideoEnabled && !isScreenSharing && IS_TOUCH_DEVICE && (
            <Button variant="ghost" size="icon" onClick={flipCamera} className="w-12 h-12 rounded-full bg-white/20 text-white hover:bg-white/30" title="Flip camera" data-testid="flip-camera"><SwitchCamera className="w-5 h-5" /></Button>
          )}
          <Button variant="ghost" size="icon" onClick={toggleScreenShare} className={'w-12 h-12 rounded-full ' + (isScreenSharing ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white/20 text-white hover:bg-white/30')} data-testid="toggle-screen-share"><Monitor className="w-5 h-5" /></Button>
          {audioRoute.routes.length > 0 && (
            <div className="relative">
              <Button variant="ghost" size="icon" onClick={function() { loadAudioRoutes(); setAudioRoute(function(prev) { return Object.assign({}, prev, { open: !prev.open }); }); }} className={'w-12 h-12 rounded-full ' + (audioRoute.current === 'speaker' ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white/20 text-white hover:bg-white/30')} title="Sound output" data-testid="audio-route-btn">
                {audioRoute.current === 'bluetooth' ? <Bluetooth className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              {audioRoute.open && (
                <div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-gray-900/95 border border-white/15 rounded-xl p-1.5 min-w-[190px] shadow-xl z-10" data-testid="audio-route-menu">
                  {(nativeAudio() ? [{ id: 'auto', label: 'Auto (ear / hand / Bluetooth)' }] : []).concat(audioRoute.routes).map(function(r) {
                    return <button key={r.id} type="button" onClick={function() { chooseAudioRoute(r.id); }} className={'w-full text-left text-sm px-3 py-2 rounded-lg ' + (audioRoute.current === r.id ? 'bg-blue-600 text-white' : 'text-white/90 hover:bg-white/10')} data-testid={'audio-route-' + r.id}>{r.label}</button>;
                  })}
                </div>
              )}
            </div>
          )}
          {canPip && (
            <Button variant="ghost" size="icon" onClick={enterPip} className="w-12 h-12 rounded-full bg-white/20 text-white hover:bg-white/30" title="Picture in picture" data-testid="pip-btn"><PictureInPicture2 className="w-5 h-5" /></Button>
          )}
          <Button variant="ghost" size="icon" onClick={handleEndCall} className="w-14 h-14 rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="end-call"><PhoneOff className="w-6 h-6" /></Button>
          </div>
        </div>
      </div>
    </>
  );
}

function PeerAudio(props) {
  var ref = useRef(null);
  useEffect(function() { if (ref.current && props.stream && ref.current.srcObject !== props.stream) ref.current.srcObject = props.stream; }, [props.stream]);
  return <audio ref={ref} autoPlay data-peer-audio="1" style={{ display: "none" }} />;
}

function PeerTile(props) {
  var ref = useRef(null);
  var info = props.info || {};
  useEffect(function() { if (ref.current && info.stream && ref.current.srcObject !== info.stream) ref.current.srcObject = info.stream; }, [info.stream]);
  var hasVideo = info.stream && info.stream.getVideoTracks().length > 0;
  return (
    <div className="relative bg-black rounded-lg overflow-hidden min-h-[120px]" data-testid={'remote-tile-' + props.userId}>
      <video ref={ref} autoPlay playsInline muted className="w-full h-full object-contain bg-black" data-testid="remote-video" />
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="w-16 h-16 rounded-full bg-green-700 text-white flex items-center justify-center text-xl font-semibold">{(info.name || '?').trim().charAt(0).toUpperCase()}</div>
        </div>
      )}
      <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
        <span className="text-white text-xs bg-black/50 rounded px-1.5 py-0.5 truncate">{info.name || 'Participant'}</span>
        {info.state !== 'connected' && <span className="text-[10px] text-amber-300 bg-black/50 rounded px-1">{info.state === 'failed' ? 'failed' : 'connecting…'}</span>}
      </div>
    </div>
  );
}
